
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Search, ChevronDown } from "lucide-react";
import { countries, formatPhoneNumber, splitPhoneNumber, detectUserCountry } from "@/utils/countryPhoneCodes";
import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  centerAlign?: boolean;
}

export const PhoneNumberInput = ({ 
  value, 
  onChange, 
  required = false,
  centerAlign = false
}: PhoneNumberInputProps) => {
  const [localNumber, setLocalNumber] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(detectUserCountry());
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // When the value prop changes, split it into country code and local number
    const { countryCode, localNumber: newLocalNumber } = splitPhoneNumber(value);
    const country = countries.find(c => c.dialCode === countryCode) || countries[0];
    setSelectedCountry(country);
    setLocalNumber(newLocalNumber);
  }, [value]);

  const handleLocalNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLocalNumber = e.target.value.replace(/\D/g, ''); // Only allow digits
    setLocalNumber(newLocalNumber);
    onChange(formatPhoneNumber(newLocalNumber, selectedCountry.dialCode));
  };

  const handleCountryChange = (countryCode: string) => {
    const country = countries.find(c => c.code === countryCode) || countries[0];
    setSelectedCountry(country);
    onChange(formatPhoneNumber(localNumber, country.dialCode));
  };

  return (
    <div className="space-y-2">
      <div className={cn(
        "flex items-center bg-white border border-gray-200 rounded-md overflow-hidden",
        centerAlign && "justify-center"
      )}>
        <Select
          value={selectedCountry.code}
          onValueChange={handleCountryChange}
        >
          <SelectTrigger className="w-[140px] border-0 bg-transparent focus:ring-0 px-3 h-14">
            <SelectValue>
              <div className="flex items-center gap-2">
                <span className="text-lg">{selectedCountry.flag}</span>
                <span className="text-gray-500">{selectedCountry.dialCode}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {countries.map((country) => (
              <SelectItem 
                key={country.code} 
                value={country.code}
                className="cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span>{country.flag}</span>
                  <span className="text-sm">{country.name}</span>
                  <span className="text-sm text-gray-500 ml-auto">
                    {country.dialCode}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="w-px bg-gray-200 self-stretch" />
        <Input
          id="phoneNumber"
          value={localNumber}
          onChange={handleLocalNumberChange}
          required={required}
          placeholder="Phone number"
          type="tel"
          className="bg-transparent border-0 text-lg h-14"
        />
      </div>
    </div>
  );
};
